/**
 * Render output sidebar panel for Render mode.
 *
 * Contains overlay settings plus export and share actions for Render mode.
 *
 * Output dimensions have been moved to the Camera panel so Render and
 * Inking modes share a single authoritative place for image size.
 */

import { Share2 } from "lucide-react";
import { useCallback } from "react";
import { useStore } from "../../app/store.ts";
import type { RenderGridOverride } from "../../app/storeTypes.ts";
import { Button, IconButton } from "../Button.tsx";
import { exportToDownload, exportToShare } from "../exportUtils.ts";
import { Divider, SectionLabel, SegmentedControl } from "../shared.tsx";
import { COLOR_TEXT_MUTED, FONT_SIZE_SMALL } from "../tokens.ts";

// ---------------------------------------------------------------------------
// RenderOutputPanelBody
// ---------------------------------------------------------------------------

export function RenderOutputPanelBody() {
  const renderGridOverride = useStore((s) => s.renderGridOverride);
  const setRenderGridOverride = useStore((s) => s.setRenderGridOverride);

  const handleExport = useCallback(() => exportToDownload(), []);

  const handleShare = useCallback(() => exportToShare(), []);

  return (
    <>
      {/* Overlays */}
      <SectionLabel>Overlays</SectionLabel>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 4,
        }}
      >
        <span
          style={{
            fontSize: FONT_SIZE_SMALL,
            color: COLOR_TEXT_MUTED,
            minWidth: 70,
          }}
        >
          Object Grids
        </span>
        <SegmentedControl<RenderGridOverride>
          options={[
            { label: "Off", value: "off" },
            { label: "Defer", value: "defer" },
            { label: "On", value: "on" },
          ]}
          value={renderGridOverride}
          onChange={setRenderGridOverride}
        />
      </div>

      {/* Export */}
      <Divider />
      <SectionLabel>Export</SectionLabel>

      <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
        <Button
          onClick={handleExport}
          title="Export render as PNG"
          variant="secondary"
          fullWidth
        >
          Export PNG
        </Button>
        <IconButton
          onClick={handleShare}
          title="Share"
          label="Share"
          icon={<Share2 size={13} />}
          variant="secondary"
          size={30}
        />
      </div>
    </>
  );
}
